// NoorView plans: what each one includes, its price, and the limits the apps and the database enforce.
// Prices match noormsystems.co.uk/#pricing (November 2026).

export type PlanId = 'free' | 'basic' | 'pro';

/** The Free plan is one person and one screen, with no time limit. Basic and Pro have no screen limit. */
export const PLAN_LIMITS: Record<PlanId, { screens: number | null; users: number | null }> = {
  free: { screens: 1, users: 1 },
  basic: { screens: null, users: 3 },
  pro: { screens: null, users: null },
};

/** Can this masjid add another screen (it has `count` already)? */
export const canAddScreen = (plan: PlanId, count: number) => PLAN_LIMITS[plan]?.screens == null || count < PLAN_LIMITS[plan].screens!;
/** Can this masjid add another person to its team (it has `count` already)? Free is one person, Basic up to three. */
export const canAddPerson = (plan: PlanId, count: number) => PLAN_LIMITS[plan]?.users == null || count < PLAN_LIMITS[plan].users!;

export const PLAN_LIMIT_TEXT = {
  screens: 'The Free plan includes one screen. Upgrade to Basic or Pro to add more screens.',
  users: 'The Free plan is for one person. Upgrade to Basic or Pro to add your team.',
  basicUsers: 'The Basic plan is for up to 3 people. Upgrade to Pro for unlimited users.',
} as const;

/** What to say when a masjid's team is full on its plan. */
export const personLimitText = (plan: PlanId) => (plan === 'basic' ? PLAN_LIMIT_TEXT.basicUsers : PLAN_LIMIT_TEXT.users);

/** The plans, as shown in NoorView's Plans sheet. */
export const PLANS = [
  { id: 'free', name: 'Free', price: '£0', note: 'Free forever', feats: ['Single user and single screen', 'Full access, no time limit (app not included)', 'Installation guides', 'Ticket support', 'Ready-to-go APK and dashboard'] },
  { id: 'basic', name: 'Basic', price: '£20', note: 'per month, or £15 a month yearly (£180)', feats: ['Everything in Free', 'Full ecosystem access', 'Unlimited screens', 'Video training', 'Up to 3 users', 'Ticket and call-back support'] },
  { id: 'pro', name: 'Pro', price: '£35', note: 'per month, or £25 a month yearly (£300)', feats: ['Everything in Basic', 'Unlimited users', 'Remote access support', 'In-person training', 'Call-back support'] },
  { id: 'own', name: 'Own NoorView', price: '£900', note: 'one-off: the same as 3 years of Pro yearly, then yours for good', feats: ['Everything in Pro', 'NoorView web dashboard and iPhone app', 'Unlimited screens and users', 'No monthly or yearly fees'] },
] as const;
